import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Check, X, ArrowsClockwise, CircleNotch, ListChecks, Sparkle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { compressImage } from '../lib/imageCompress';
import { API } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { useUpgrade } from '../context/UpgradeContext';

/**
 * Walk-around capture — speed-snagging mode.
 *
 * The slow path is "Log new defect → room picker → photo → save". That
 * takes ~4 taps per defect. On a first walk-through of a property a
 * homeowner often spots 20–40 issues in 15 minutes. The 4-tap flow
 * murders momentum.
 *
 * Walk-around mode collapses that to 2 taps per defect:
 *   1. Tap "Capture" → file input opens (or camera on mobile)
 *   2. Photo selected → instantly added to a session queue
 *
 * When the user is done, they tap "Save N defects" and we batch-create
 * the queued defects. Each defect:
 *   - Title defaults to "Defect #<count> · <room name>"
 *   - Severity defaults to "minor"
 *   - Room is the LAST-USED room from the walk-around picker, or the
 *     property's first room if nothing chosen yet
 *   - The homeowner can edit titles + severities + rooms later via the
 *     normal SnagForm edit flow on each row
 *
 * Deferred (Phase 4): AI auto-tagging via Gemini per photo. The hook
 * point is `applyAiSuggestion()` near `enqueue()` — leave a comment
 * saying so for the next iteration.
 *
 * Photo annotations: NOT exposed in walk-around mode (you wouldn't want
 * to draw arrows mid-walk). The user can open any captured defect later
 * and annotate via the standard edit flow.
 */

/** Read a File (from <input type="file">) into a dataURL string so the
 *  existing dataURL-based compressImage helper can downscale it. */
function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
        reader.readAsDataURL(file);
    });
}

export default function WalkAroundCapture({ propertyId, rooms, onSubmit, onClose }) {
    const fileInputRef = useRef(null);
    const [queue, setQueue] = useState([]);            // [{id, dataUrl, roomId, processing, aiTitle, aiSeverity, aiState}]
    const [activeRoomId, setActiveRoomId] = useState(rooms?.[0]?.id || null);
    const [processingCount, setProcessingCount] = useState(0);
    const [saving, setSaving] = useState(false);
    const upgrade = useUpgrade();
    const ent = useEntitlements();

    // Foundation users don't get walk-around AI Suggest — it's a paid
    // feature. Their 3 lifetime AI calls stay reserved for full defect
    // analysis on individual snags. Paid tiers (rectify/surveyor/
    // architect/developer_team) get unlimited walk-around Suggests on
    // unlocked properties.
    const isFoundation = (ent?.tier || 'foundation') === 'foundation';
    const walkaroundAiLocked = isFoundation;  // always locked for foundation

    // Open the OS camera/photo picker immediately when the modal mounts —
    // saves one tap and feels native. We re-open it after every capture
    // too (see below).
    useEffect(() => {
        const t = setTimeout(() => fileInputRef.current?.click(), 250);
        return () => clearTimeout(t);
    }, []);

    const enqueue = async (file) => {
        if (!file) return;
        const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const draft = {
            id: localId,
            dataUrl: null,
            roomId: activeRoomId,
            processing: true,
        };
        setQueue(q => [...q, draft]);
        setProcessingCount(n => n + 1);
        try {
            // compressImage expects a dataURL string — convert the File
            // first via FileReader, then downscale + re-encode.
            const rawDataUrl = await fileToDataUrl(file);
            const dataUrl = await compressImage(rawDataUrl);
            setQueue(q => q.map(d => d.id === localId ? { ...d, dataUrl, processing: false } : d));
            // Hook point — when AI auto-tagging is added later, fire it
            // here and patch the queue entry with { title, severity }.
            // For now leave defaults and let the user edit after save.
        } catch (err) {
            setQueue(q => q.filter(d => d.id !== localId));
            toast.error('Could not process that photo. Try again.');
        } finally {
            setProcessingCount(n => Math.max(0, n - 1));
        }
    };

    const onFile = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';   // reset so picking the same file twice re-fires
        if (files.length === 0) return;
        for (const f of files) await enqueue(f);
    };

    const removeFromQueue = (id) => setQueue(q => q.filter(d => d.id !== id));

    const reAssignRoom = (id, roomId) => setQueue(q => q.map(d => d.id === id ? { ...d, roomId } : d));

    /** Ask Gemini for a title + severity based on the photo. Opt-in per
     *  item so the user controls when they spend their AI budget. */
    const suggestTag = async (item) => {
        if (!item?.dataUrl) return;
        setQueue(q => q.map(d => d.id === item.id ? { ...d, aiState: 'loading' } : d));
        try {
            const roomName = rooms.find(r => r.id === item.roomId)?.name || '';
            const res = await axios.post(`${API}/walkaround/suggest-tag`, {
                photo_base64: item.dataUrl,
                room_name: roomName,
                property_id: propertyId || null,
            }, { withCredentials: true });
            const { title, severity } = res.data?.suggestion || {};
            setQueue(q => q.map(d => d.id === item.id
                ? { ...d, aiTitle: title || d.aiTitle, aiSeverity: severity || d.aiSeverity, aiState: 'applied' }
                : d));
            toast.success(`Tagged: ${title} · ${severity}`);
        } catch (err) {
            setQueue(q => q.map(d => d.id === item.id ? { ...d, aiState: 'error' } : d));
            const status = err?.response?.status;
            const code = err?.response?.data?.detail?.code;
            if (status === 402) {
                // Foundation walk-around lock OR Rectify-per-property lock.
                upgrade.show({ reason: code || 'walkaround_ai_paid_only' });
            } else {
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : 'AI tag failed. Try again.');
            }
        }
    };

    const save = async () => {
        if (queue.length === 0) return;
        if (processingCount > 0) {
            toast('Hold on — photos still processing.');
            return;
        }
        setSaving(true);
        // Hand the batch off to the parent — it handles store.addSnag()
        // calls one by one so the optimistic-UI queue + offline-sync
        // path is preserved. We just feed it the data.
        try {
            await onSubmit(queue.map((d, i) => {
                const roomName = rooms.find(r => r.id === d.roomId)?.name;
                const fallbackTitle = `Defect #${i + 1}${roomName ? ` · ${roomName}` : ''}`;
                return {
                    title: d.aiTitle || fallbackTitle,
                    roomId: d.roomId,
                    notes: '',
                    severity: d.aiSeverity || 'minor',
                    photo: d.dataUrl,
                    photo_annotations: [],
                };
            }));
            toast.success(`Captured ${queue.length} defect${queue.length === 1 ? '' : 's'}.`);
            onClose?.();
        } catch (err) {
            toast.error('Could not save defects. Try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" data-testid="walkaround-capture">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 bg-black text-white border-b border-[#27272A]">
                <button type="button" onClick={onClose} aria-label="Close" data-testid="walkaround-close" className="p-2 -ml-2 hover:bg-white/10">
                    <X size={20} weight="bold" />
                </button>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] text-center">
                    <div>Walk-around · {queue.length} defect{queue.length === 1 ? '' : 's'}</div>
                    {isFoundation && (
                        <div className="text-[8px] mt-0.5 text-[#F59E0B]" data-testid="walkaround-ai-meter">
                            <Sparkle size={9} weight="fill" className="inline-block -mt-0.5 mr-0.5" />
                            AI Suggest · Rectify or Surveyor
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || queue.length === 0 || processingCount > 0}
                    data-testid="walkaround-save"
                    className="inline-flex items-center gap-1.5 bg-[#1FA5CF] text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 disabled:opacity-50"
                >
                    {saving ? (<><CircleNotch size={12} weight="bold" className="animate-spin" /> Saving…</>) : (<><Check size={14} weight="bold" /> Save</>)}
                </button>
            </div>

            {/* Active-room picker (defaults the room for each new capture) */}
            {rooms?.length > 0 && (
                <div className="px-3 py-2 bg-[#0E0E10] border-b border-[#27272A] flex items-center gap-2 overflow-x-auto">
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#A1A1AA] flex-none mr-1">Room:</span>
                    {rooms.map(r => (
                        <button
                            type="button"
                            key={r.id}
                            onClick={() => setActiveRoomId(r.id)}
                            data-testid={`walkaround-room-${r.id}`}
                            className={`flex-none px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${
                                activeRoomId === r.id ? 'bg-[#1FA5CF] text-white' : 'text-[#A1A1AA] border border-[#27272A]'
                            }`}
                        >{r.name}</button>
                    ))}
                </div>
            )}

            {/* Queue grid */}
            <div className="flex-1 overflow-auto p-3">
                {queue.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-[#A1A1AA] text-center">
                        <Camera size={48} weight="duotone" />
                        <p className="mt-3 text-sm">Capturing photos as defects.</p>
                        <p className="mt-1 text-xs">Tap the camera button below for each one.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-3xl mx-auto" data-testid="walkaround-queue">
                        {queue.map((d, i) => {
                            const canSuggest = !d.processing && d.dataUrl && d.aiState !== 'loading' && d.aiState !== 'applied';
                            const suggestBlocked = walkaroundAiLocked;
                            return (
                            <div key={d.id} className="relative bg-[#18181B] aspect-square overflow-hidden" data-testid={`walkaround-queue-item-${i}`}>
                                {d.processing ? (
                                    <div className="absolute inset-0 flex items-center justify-center text-[#A1A1AA]">
                                        <CircleNotch size={28} weight="bold" className="animate-spin" />
                                    </div>
                                ) : (
                                    <img src={d.dataUrl} alt={`Defect ${i + 1}`} className="w-full h-full object-cover" />
                                )}
                                <div className="absolute top-1 left-1 bg-black/70 text-white px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em]">
                                    #{i + 1}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeFromQueue(d.id)}
                                    aria-label="Remove"
                                    data-testid={`walkaround-remove-${i}`}
                                    className="absolute top-1 right-1 bg-black/70 hover:bg-[#E53935] text-white p-1"
                                >
                                    <X size={12} weight="bold" />
                                </button>

                                {/* AI-tagged preview strip: shows over the top when we
                                    have a Gemini suggestion so the user can see what
                                    the defect will be titled + how severe. */}
                                {d.aiTitle && (
                                    <div
                                        className="absolute top-8 left-1 right-1 bg-black/80 border-l-2 border-[#1FA5CF] px-1.5 py-1 text-white pointer-events-none"
                                        data-testid={`walkaround-ai-tag-${i}`}
                                    >
                                        <div className="text-[10px] font-semibold leading-tight line-clamp-2">{d.aiTitle}</div>
                                        <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#67E8F9] mt-0.5">{d.aiSeverity}</div>
                                    </div>
                                )}

                                {/* Suggest / retry pill — bottom-right above the room
                                    dropdown. Foundation gets a nudge when their AI
                                    budget is spent. */}
                                {canSuggest && !suggestBlocked && (
                                    <button
                                        type="button"
                                        onClick={() => suggestTag(d)}
                                        data-testid={`walkaround-suggest-${i}`}
                                        aria-label="AI suggest title and severity"
                                        className="absolute bottom-6 right-1 inline-flex items-center gap-1 bg-[#1FA5CF] hover:bg-[#1787AB] text-white px-1.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] active:scale-95 transition-transform"
                                    >
                                        <Sparkle size={11} weight="fill" />
                                        {d.aiState === 'error' ? 'Retry' : 'Suggest'}
                                    </button>
                                )}
                                {canSuggest && suggestBlocked && (
                                    <button
                                        type="button"
                                        onClick={() => upgrade.show({ reason: 'walkaround_ai_paid_only' })}
                                        data-testid={`walkaround-suggest-locked-${i}`}
                                        aria-label="Upgrade for AI suggestions"
                                        className="absolute bottom-6 right-1 inline-flex items-center gap-1 bg-black/70 hover:bg-black text-[#F59E0B] px-1.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] active:scale-95 transition-transform border border-[#F59E0B]"
                                    >
                                        <Warning size={11} weight="fill" />
                                        Upgrade
                                    </button>
                                )}
                                {d.aiState === 'loading' && (
                                    <div className="absolute bottom-6 right-1 bg-[#1FA5CF]/90 text-white px-1.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] inline-flex items-center gap-1 pointer-events-none">
                                        <CircleNotch size={11} weight="bold" className="animate-spin" /> AI…
                                    </div>
                                )}

                                {/* Per-item room override (small dropdown) */}
                                {rooms?.length > 1 && (
                                    <select
                                        value={d.roomId || ''}
                                        onChange={(e) => reAssignRoom(d.id, e.target.value)}
                                        aria-label="Room"
                                        data-testid={`walkaround-room-select-${i}`}
                                        className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] font-mono uppercase tracking-[0.18em] px-1 py-0.5 outline-none"
                                    >
                                        {rooms.map(r => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        );})}
                    </div>
                )}
            </div>

            {/* Capture button — fixed at the bottom */}
            <div className="bg-black px-3 py-3 border-t border-[#27272A] flex items-center justify-between max-w-3xl mx-auto w-full">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA] flex items-center gap-2">
                    <ListChecks size={14} weight="duotone" />
                    {queue.length} queued
                </div>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    data-testid="walkaround-capture-btn"
                    className="inline-flex items-center gap-2 bg-[#1FA5CF] text-white px-5 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 disabled:opacity-50"
                >
                    <Camera size={16} weight="bold" />
                    Capture next
                </button>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Open camera"
                    data-testid="walkaround-refire"
                    className="p-2 text-[#A1A1AA] hover:bg-white/10"
                    disabled={saving}
                >
                    <ArrowsClockwise size={16} weight="bold" />
                </button>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={onFile}
            />
        </div>
    );
}
